import React from "react";
import { Compass, Scale, Calculator, Globe2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";

const SERVICES = [
  {
    icon: Compass,
    title: "Lifestyle & investment guidance",
    text: "A structured conversation about why you are buying — second home, yield, Golden Visa or legacy — and what that means for emirate, district and product.",
  },
  {
    icon: Scale,
    title: "Market comparison & due diligence",
    text: "Like-for-like district comparisons, developer track-record reviews, net-yield modelling and honest reads on pricing before you commit.",
  },
  {
    icon: Calculator,
    title: "Numbers-first underwriting",
    text: "Acquisition costs, service charges, realistic rents and exit liquidity — modelled clearly, in your currency, with risks on the table.",
  },
  {
    icon: Globe2,
    title: "Cross-border coordination",
    text: "Introductions to independent tax, legal and mortgage specialists for UK, Indian, US and other international buyers purchasing remotely.",
  },
];

const Advisory = () => (
  <div data-testid="advisory-page">
    <SEO
      title="UAE Property Advisory & Investment Guidance"
      description="Buying UAE property from overseas involves more than finding the right unit. ACRE helps buyers understand the market, compare options and make informed decisions."
      path="/advisory"
      keywords="Dubai property investment for UK buyers, buying property in Dubai as a UK citizen, tax-free property investment UAE, UAE Golden Visa property"
    />
    <PageHero
      eyebrow="Advisory"
      title="UAE Property Advisory & Investment Guidance"
      intro="Buying UAE property from overseas involves more than finding the right unit. ACRE helps buyers understand the market, compare options and make informed decisions."
      testid="advisory-hero"
    />

    <section className="pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 2) * 0.1}>
              <div className="card-hover h-full bg-[#13151D] border border-[rgba(197,168,128,0.15)] rounded-2xl p-9" data-testid={`advisory-service-${i + 1}`}>
                <s.icon size={28} className="text-[#E6CA65] mb-6" strokeWidth={1.5} />
                <h2 className="font-display text-2xl text-[#F4F3EF] mb-4">{s.title}</h2>
                <p className="text-sm text-[#9DA3AE] font-light leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="eyebrow mb-4">How we work</p>
              <h2 className="font-display text-3xl sm:text-4xl font-light text-[#F4F3EF] leading-tight mb-6">
                Advisory first, brokerage second
              </h2>
              <p className="text-[#9DA3AE] font-light leading-relaxed mb-5">
                Every engagement begins with a private consultation — a candid discussion of goals, budget, timeline and
                concerns. Only then do we curate. If the right answer is to wait, or to buy smaller, or to look at a
                different emirate entirely, that is the advice you will hear.
              </p>
              <p className="text-[#9DA3AE] font-light leading-relaxed">
                Our guidance is educational, not directive: ACRE does not provide financial, legal, tax or immigration
                advice, and we encourage every client to take independent professional counsel where required.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[rgba(197,168,128,0.2)]">
              <img
                src="https://images.unsplash.com/photo-1776362355123-ca966d36e29c?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Luxury branded residence interior in Dubai"
                loading="lazy"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-16 text-center">
            <Link
              to="/insights"
              data-testid="advisory-insights-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C5A880] hover:text-[#E6CA65] transition-colors"
            >
              Read ACRE Insights for buyer guides <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CTABand testid="advisory-cta" />
  </div>
);

export default Advisory;
